/**
 * Aba "Meus templates" da Biblioteca de modelos.
 *
 * Layout 2 colunas: árvore de pastas (esq) + grid de templates da pasta (dir).
 * Escopo por projeto (seletor no topo) ou pessoal (sem projeto).
 *
 * Inclui dialogs de criar/renomear/mover/excluir pasta, criar/editar/excluir
 * template, todos inline aqui pra centralizar o estado da aba.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Plus, Loader2, Pencil, Trash2, FolderInput,
    Files, AlertCircle, Upload, Download, X, CheckCircle2, Circle, RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
    getProjects,
    getFolderTree, createFolder, updateFolder, deleteFolder,
    listCustomTemplates, deleteCustomTemplate, batchMoveCustomTemplates,
    batchDeleteCustomTemplates,
    getTemplateApprovalSummary,
    getFolderApprovalStatus,
    type TemplateApprovalCounts,
} from '@/services/api';
import type { CustomTemplate, TemplateFolderNode } from '@/types/template-library';
import FolderTree, { flattenFolders } from './FolderTree';
import CustomTemplateFormDialog from './CustomTemplateFormDialog';
import ImportTemplatesDialog from './ImportTemplatesDialog';
import SyncFromFlowDialog from './SyncFromFlowDialog';
import { MetaErrorBreakdown } from './MetaErrorBreakdown';

export default function MyTemplatesTab() {
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [projectId, setProjectId] = useState<number | null>(null);
    const [folderId, setFolderId] = useState<number | null>(null);

    // Dialog states
    const [templateDialog, setTemplateDialog] = useState<{
        open: boolean; mode: 'create' | 'edit'; template: CustomTemplate | null;
    }>({ open: false, mode: 'create', template: null });

    const [folderDialog, setFolderDialog] = useState<{
        open: boolean; mode: 'create' | 'rename'; parentId: number | null; folder: TemplateFolderNode | null;
    }>({ open: false, mode: 'create', parentId: null, folder: null });

    const [moveDialog, setMoveDialog] = useState<{ open: boolean; folder: TemplateFolderNode | null }>(
        { open: false, folder: null },
    );
    const [deleteFolderTarget, setDeleteFolderTarget] = useState<TemplateFolderNode | null>(null);
    const [deleteTemplateTarget, setDeleteTemplateTarget] = useState<CustomTemplate | null>(null);

    // Import/gerar em massa + seleção em massa + sync do flow
    const [importOpen, setImportOpen] = useState(false);
    const [syncOpen, setSyncOpen] = useState(false);

    // Filtro por status Meta da pasta (só vale quando dentro de uma pasta)
    const [metaFilter, setMetaFilter] = useState<'all' | 'approved' | 'rejected' | 'pending' | 'none'>('all');
    // Escopo: agrega approvals de esteira, broadcast (free) ou ambos
    const [approvalScope, setApprovalScope] = useState<'all' | 'esteira' | 'broadcast'>('all');
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [bulkMoveOpen, setBulkMoveOpen] = useState(false);
    const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

    // Limpa seleção quando troca de pasta/projeto
    useEffect(() => { setSelectedIds(new Set()); }, [projectId, folderId]);

    // ─── Queries ───────────────────────────────────────────────
    const { data: projects = [] } = useQuery({
        queryKey: ['template-library-projects'],
        queryFn: getProjects,
        staleTime: 1000 * 60 * 5,
    });

    const { data: tree, isLoading: treeLoading } = useQuery({
        queryKey: ['folder-tree', projectId],
        queryFn: () => getFolderTree(projectId),
        staleTime: 1000 * 15,
    });

    const { data: templatesResp, isLoading: templatesLoading } = useQuery({
        queryKey: ['custom-templates', projectId, folderId],
        queryFn: () => listCustomTemplates(projectId, folderId),
        staleTime: 1000 * 15,
    });

    // Status agregado de aprovação Meta por template (lookup 1 query batch).
    // Renderizado como badge no canto do card.
    const templateIds = (templatesResp?.items ?? []).map((t) => t.id);
    const { data: approvalSummary } = useQuery({
        queryKey: ['custom-templates-approval-summary', templateIds.join(','), approvalScope],
        queryFn: () => getTemplateApprovalSummary(templateIds, approvalScope),
        enabled: templateIds.length > 0,
        staleTime: 1000 * 30,
    });

    // Status agregado da pasta (informacional — submit é automático via esteira)
    const { data: folderApprovalStatus } = useQuery({
        queryKey: ['folder-approval-status', folderId, approvalScope],
        queryFn: () => getFolderApprovalStatus(folderId!, approvalScope),
        enabled: folderId != null,
        staleTime: 1000 * 30,
    });

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ['folder-tree', projectId] });
        queryClient.invalidateQueries({ queryKey: ['custom-templates'] });
    };

    // ─── Folder mutations ──────────────────────────────────────
    // Create: aceita projectId vindo do dialog (default = filtro atual da tela).
    // Rename: só name; projectId é ignorado.
    const folderMutation = useMutation({
        mutationFn: async (vars: {
            mode: 'create' | 'rename';
            name: string;
            parentId: number | null;
            projectId?: number | null;
            folderId?: number;
        }) => {
            if (vars.mode === 'create') {
                return createFolder({
                    name: vars.name,
                    project_id: vars.projectId ?? null,
                    parent_id: vars.parentId,
                });
            }
            return updateFolder(vars.folderId!, { name: vars.name });
        },
        onSuccess: () => {
            toast({ title: 'Pasta salva' });
            invalidate();
            setFolderDialog({ open: false, mode: 'create', parentId: null, folder: null });
        },
        onError: (e: any) => toast({
            title: 'Falha ao salvar pasta',
            description: e?.response?.data?.detail || e?.message, variant: 'destructive',
        }),
    });

    // Move dentro do mesmo escopo (muda parent_id, mantém project_id).
    const moveMutation = useMutation({
        mutationFn: (vars: { folderId: number; parentId: number | null }) =>
            updateFolder(vars.folderId, { parent_id: vars.parentId }),
        onSuccess: () => {
            toast({ title: 'Pasta movida' });
            invalidate();
            setMoveDialog({ open: false, folder: null });
        },
        onError: (e: any) => toast({
            title: 'Falha ao mover',
            description: e?.response?.data?.detail || e?.message, variant: 'destructive',
        }),
    });

    // Migra pasta + subpastas + templates dentro pra outro escopo (cascata).
    // Backend zera o parent_id da pasta migrada (vira raiz no destino).
    const migrateProjectMutation = useMutation({
        mutationFn: (vars: { folderId: number; newProjectId: number | null }) =>
            updateFolder(vars.folderId, { project_id: vars.newProjectId }),
        onSuccess: (_d, vars) => {
            toast({ title: 'Pasta migrada de projeto' });
            invalidate();
            setMoveDialog({ open: false, folder: null });
            // Já leva o user pro novo escopo, pra ele ver a pasta lá
            setProjectId(vars.newProjectId);
            setFolderId(null);
        },
        onError: (e: any) => toast({
            title: 'Falha ao migrar',
            description: e?.response?.data?.detail || e?.message, variant: 'destructive',
        }),
    });

    const deleteFolderMutation = useMutation({
        mutationFn: (id: number) => deleteFolder(id),
        onSuccess: (_d, id) => {
            toast({ title: 'Pasta excluída' });
            if (folderId === id) setFolderId(null);
            invalidate();
            setDeleteFolderTarget(null);
        },
        onError: (e: any) => toast({
            title: 'Falha ao excluir',
            description: e?.response?.data?.detail || e?.message, variant: 'destructive',
        }),
    });

    const deleteTemplateMutation = useMutation({
        mutationFn: (id: number) => deleteCustomTemplate(id),
        onSuccess: () => {
            toast({ title: 'Template excluído' });
            invalidate();
            setDeleteTemplateTarget(null);
        },
        onError: (e: any) => toast({
            title: 'Falha ao excluir',
            description: e?.response?.data?.detail || e?.message, variant: 'destructive',
        }),
    });

    const bulkMoveMutation = useMutation({
        mutationFn: (vars: { ids: number[]; folderId: number | null }) =>
            batchMoveCustomTemplates({ ids: vars.ids, folder_id: vars.folderId }),
        onSuccess: (r) => {
            toast({ title: `${r.moved} ${r.moved === 1 ? 'template movido' : 'templates movidos'}` });
            setSelectedIds(new Set());
            invalidate();
            setBulkMoveOpen(false);
        },
        onError: (e: any) => toast({
            title: 'Falha ao mover',
            description: e?.response?.data?.detail || e?.message, variant: 'destructive',
        }),
    });

    const bulkDeleteMutation = useMutation({
        mutationFn: (ids: number[]) => batchDeleteCustomTemplates({ ids }),
        onSuccess: (r) => {
            const msg = r.skipped
                ? `${r.deleted} excluído${r.deleted === 1 ? '' : 's'} · ${r.skipped} ignorado${r.skipped === 1 ? '' : 's'}`
                : `${r.deleted} ${r.deleted === 1 ? 'template excluído' : 'templates excluídos'}`;
            toast({ title: msg });
            setSelectedIds(new Set());
            invalidate();
            setBulkDeleteOpen(false);
        },
        onError: (e: any) => toast({
            title: 'Falha ao excluir',
            description: e?.response?.data?.detail || e?.message, variant: 'destructive',
        }),
    });

    const allTemplates = templatesResp?.items ?? [];

    // Categoriza um template pelo agregado de approvals: approved (todos
    // approved), rejected (ao menos 1 rejected/errored), pending (só
    // pending), none (sem submit ainda).
    const categorize = (id: number): 'approved' | 'rejected' | 'pending' | 'none' => {
        const c = approvalSummary?.[String(id)];
        if (!c || c.total === 0) return 'none';
        if (c.rejected + c.errored > 0) return 'rejected';
        if (c.approved === c.total) return 'approved';
        return 'pending';
    };

    const templates = metaFilter === 'all'
        ? allTemplates
        : allTemplates.filter((t) => categorize(t.id) === metaFilter);
    const flatFolders = useMemo(() => flattenFolders(tree?.folders ?? []), [tree]);
    const folderLabel = folderId === null
        ? 'Raiz'
        : (flatFolders.find((f) => f.id === folderId)?.name ?? 'Pasta');

    // ─── Seleção em massa ──────────────────────────────────────
    const toggleSelect = (id: number) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const allSelected = templates.length > 0 && templates.every((t) => selectedIds.has(t.id));
    const toggleSelectAll = () => {
        setSelectedIds(allSelected ? new Set() : new Set(templates.map((t) => t.id)));
    };

    // ─── Export JSON (shape limpo reimportável) ────────────────
    const exportTemplates = (list: CustomTemplate[]) => {
        const clean = list.map((t) => ({
            name: t.name,
            template_type: t.template_type,
            title: t.title || undefined,
            subtitle: t.subtitle || undefined,
            image_url: t.image_url || undefined,
            text_content: t.text_content || undefined,
            buttons: (t.buttons_json || []).map((b) => ({ title: b.title || '', url: b.url || '' })),
        }));
        const blob = new Blob([JSON.stringify(clean, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `templates-${folderLabel.toLowerCase().replace(/\s+/g, '-')}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-4">
            {/* Seletor de projeto */}
            <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Projeto:</span>
                <div className="min-w-[240px]">
                    <SearchableSelect
                        value={projectId == null ? '' : String(projectId)}
                        onValueChange={(v) => { setProjectId(v ? Number(v) : null); setFolderId(null); }}
                        options={[
                            { value: '', label: 'Pessoal (sem projeto)' },
                            ...projects.map((p) => ({ value: String(p.id), label: p.name })),
                        ]}
                        placeholder="Selecione o projeto"
                        searchPlaceholder="Buscar projeto..."
                        className="h-9"
                    />
                </div>
            </div>

            {/* Layout 2 colunas */}
            <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4">
                {/* Árvore */}
                <div className="border rounded-xl bg-white p-2 h-fit">
                    {treeLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-5 h-5 animate-spin text-violet-600" />
                        </div>
                    ) : (
                        <FolderTree
                            folders={tree?.folders ?? []}
                            rootTemplateCount={tree?.root_template_count ?? 0}
                            selectedFolderId={folderId}
                            onSelect={setFolderId}
                            onCreateSubfolder={(parentId) =>
                                setFolderDialog({ open: true, mode: 'create', parentId, folder: null })}
                            onRename={(folder) =>
                                setFolderDialog({ open: true, mode: 'rename', parentId: folder.parent_id ?? null, folder })}
                            onMove={(folder) => setMoveDialog({ open: true, folder })}
                            onDelete={(folder) => setDeleteFolderTarget(folder)}
                        />
                    )}
                </div>

                {/* Grid de templates */}
                <div className="space-y-3">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-3">
                            {templates.length > 0 && (
                                <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
                                    <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} />
                                    Todos
                                </label>
                            )}
                            <div className="text-sm text-gray-500">
                                {metaFilter === 'all'
                                    ? `${allTemplates.length} ${allTemplates.length === 1 ? 'template' : 'templates'}${folderId === null ? ' na raiz' : ' nesta pasta'}`
                                    : `${templates.length} de ${allTemplates.length} (filtro ativo)`}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" onClick={() => setSyncOpen(true)}>
                                <RefreshCw className="w-4 h-4 mr-1.5" /> Sincronizar do Flow
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}>
                                <Upload className="w-4 h-4 mr-1.5" /> Importar / gerar
                            </Button>
                            <Button
                                size="sm" variant="outline"
                                onClick={() => exportTemplates(templates)}
                                disabled={templates.length === 0}
                            >
                                <Download className="w-4 h-4 mr-1.5" /> Exportar
                            </Button>
                            <Button
                                size="sm"
                                onClick={() => setTemplateDialog({ open: true, mode: 'create', template: null })}
                            >
                                <Plus className="w-4 h-4 mr-1.5" /> Novo template
                            </Button>
                        </div>
                    </div>

                    {/* Toggle escopo: agrega approvals de esteira, broadcast (free) ou ambos */}
                    {allTemplates.length > 0 && (
                        <div className="flex items-center gap-1.5 text-xs">
                            <span className="text-gray-500">Escopo Meta:</span>
                            {([
                                { key: 'all', label: 'Todos' },
                                { key: 'esteira', label: 'Esteira' },
                                { key: 'broadcast', label: 'Broadcast' },
                            ] as const).map(({ key, label }) => (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => setApprovalScope(key)}
                                    className={`px-2 py-0.5 rounded border transition ${
                                        approvalScope === key
                                            ? 'bg-violet-100 border-violet-300 text-violet-800 font-medium'
                                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Status agregado da pasta (só quando dentro de uma pasta) — aprovação é automática quando esteira usa a pasta */}
                    {folderId !== null && folderApprovalStatus && folderApprovalStatus.counts.total > 0 && (
                        <div className="flex items-center gap-3 px-3 py-2 rounded-lg border bg-violet-50/40 text-xs text-gray-700">
                            <span className="font-medium">Meta:</span>
                            <span className="text-emerald-700">✓ {folderApprovalStatus.counts.approved} aprovado</span>
                            {folderApprovalStatus.counts.pending > 0 && (
                                <span className="text-amber-700">⏳ {folderApprovalStatus.counts.pending} pendente</span>
                            )}
                            {(folderApprovalStatus.counts.rejected + folderApprovalStatus.counts.errored) > 0 && (
                                <span className="text-red-700">
                                    ✗ {folderApprovalStatus.counts.rejected + folderApprovalStatus.counts.errored} rejeitado/erro
                                </span>
                            )}
                            {folderApprovalStatus.esteiras.length > 0 && (
                                <span className="text-gray-500">
                                    · usada em {folderApprovalStatus.esteiras.length} esteira{folderApprovalStatus.esteiras.length !== 1 ? 's' : ''}
                                </span>
                            )}
                            <button
                                type="button"
                                onClick={() => {
                                    queryClient.invalidateQueries({ queryKey: ['folder-approval-status', folderId] });
                                    queryClient.invalidateQueries({ queryKey: ['folder-meta-error-breakdown', folderId] });
                                    queryClient.invalidateQueries({ queryKey: ['custom-templates-approval-summary'] });
                                }}
                                className="ml-auto inline-flex items-center gap-1 text-gray-500 hover:text-violet-700 hover:bg-violet-100 px-2 py-0.5 rounded transition"
                                title="Atualizar status Meta"
                            >
                                <RefreshCw className="w-3.5 h-3.5" /> atualizar
                            </button>
                        </div>
                    )}

                    {folderId !== null && folderApprovalStatus && (
                        <MetaErrorBreakdown
                            folderId={folderId}
                            scope={approvalScope}
                            hasErrors={(folderApprovalStatus.counts.rejected + folderApprovalStatus.counts.errored) > 0}
                        />
                    )}

                    {/* Filtro por status Meta */}
                    {folderId !== null && allTemplates.length > 0 && (
                        <div className="flex items-center gap-1.5 flex-wrap">
                            {([
                                { key: 'all', label: 'Todos', activeCls: 'bg-gray-200 text-gray-800 border-gray-300 font-medium' },
                                { key: 'approved', label: '✓ Aprovados', activeCls: 'bg-emerald-100 text-emerald-700 border-emerald-300 font-medium' },
                                { key: 'pending', label: '⏳ Pendentes', activeCls: 'bg-amber-100 text-amber-700 border-amber-300 font-medium' },
                                { key: 'rejected', label: '✗ Rejeitados/Erro', activeCls: 'bg-red-100 text-red-700 border-red-300 font-medium' },
                                { key: 'none', label: '— Não submetidos', activeCls: 'bg-gray-200 text-gray-800 border-gray-300 font-medium' },
                            ] as const).map(({ key, label, activeCls }) => {
                                const active = metaFilter === key;
                                const cnt = key === 'all'
                                    ? allTemplates.length
                                    : allTemplates.filter((t) => categorize(t.id) === key).length;
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => setMetaFilter(key)}
                                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                                            active ? activeCls : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                        }`}
                                    >
                                        {label} <span className="opacity-60">({cnt})</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {templates.length > 0 && (
                        <p className="text-xs text-gray-400 px-1">Clique num modelo pra selecionar.</p>
                    )}

                    {templatesLoading ? (
                        <div className="flex items-center justify-center py-16">
                            <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
                        </div>
                    ) : templates.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-center border rounded-xl bg-white">
                            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                                <Files className="w-6 h-6 text-gray-400" />
                            </div>
                            <h3 className="text-sm font-medium text-gray-900">Nenhum template aqui</h3>
                            <p className="text-xs text-gray-500 mt-1 mb-3 max-w-xs">
                                Crie um template do zero ou salve um da aba "Explorar".
                            </p>
                            <Button size="sm" variant="outline"
                                onClick={() => setTemplateDialog({ open: true, mode: 'create', template: null })}>
                                <Plus className="w-4 h-4 mr-1.5" /> Novo template
                            </Button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                            {templates.map((t) => (
                                <CustomTemplateCard
                                    key={t.id}
                                    template={t}
                                    selected={selectedIds.has(t.id)}
                                    approvalCounts={approvalSummary?.[String(t.id)]}
                                    onToggleSelect={() => toggleSelect(t.id)}
                                    onEdit={() => setTemplateDialog({ open: true, mode: 'edit', template: t })}
                                    onDelete={() => setDeleteTemplateTarget(t)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Barra de ações FIXA no rodapé (visível ao rolar) */}
            {selectedIds.size > 0 && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-white border shadow-xl rounded-full pl-5 pr-2 py-2">
                    <span className="text-sm font-semibold text-violet-700">
                        {selectedIds.size} selecionado{selectedIds.size > 1 ? 's' : ''}
                    </span>
                    <Button size="sm" onClick={() => setBulkMoveOpen(true)} className="rounded-full">
                        <FolderInput className="w-4 h-4 mr-1.5" /> Mover para pasta
                    </Button>
                    <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => setBulkDeleteOpen(true)}
                        className="rounded-full"
                    >
                        <Trash2 className="w-4 h-4 mr-1.5" /> Excluir
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} className="rounded-full">
                        <X className="w-3.5 h-3.5 mr-1" /> Limpar
                    </Button>
                </div>
            )}

            {/* ── Dialogs ── */}
            <CustomTemplateFormDialog
                open={templateDialog.open}
                onOpenChange={(o) => setTemplateDialog((s) => ({ ...s, open: o }))}
                mode={templateDialog.mode}
                template={templateDialog.template}
                projectId={projectId}
                folderId={folderId}
                onSaved={invalidate}
            />

            <FolderFormDialog
                state={folderDialog}
                projects={projects}
                defaultProjectId={projectId}
                onClose={() => setFolderDialog({ open: false, mode: 'create', parentId: null, folder: null })}
                onSubmit={(name, projectIdFromDialog) => folderMutation.mutate({
                    mode: folderDialog.mode,
                    name,
                    parentId: folderDialog.parentId,
                    projectId: projectIdFromDialog,
                    folderId: folderDialog.folder?.id,
                })}
                saving={folderMutation.isPending}
            />

            <MoveFolderDialog
                open={moveDialog.open}
                folder={moveDialog.folder}
                flatFolders={flatFolders}
                projects={projects}
                onClose={() => setMoveDialog({ open: false, folder: null })}
                onMovePasta={(parentId) => moveMutation.mutate({ folderId: moveDialog.folder!.id, parentId })}
                onMigrateProject={(newProjectId) => migrateProjectMutation.mutate({
                    folderId: moveDialog.folder!.id,
                    newProjectId,
                })}
                saving={moveMutation.isPending || migrateProjectMutation.isPending}
            />

            {/* Importar / gerar em massa */}
            <ImportTemplatesDialog
                open={importOpen}
                onOpenChange={setImportOpen}
                projectId={projectId}
                folderId={folderId}
                folderLabel={folderLabel}
                baseTemplates={templates}
                onCreated={invalidate}
            />

            {/* Sincronizar do Flow */}
            <SyncFromFlowDialog
                open={syncOpen}
                onOpenChange={setSyncOpen}
                projectId={projectId}
                defaultFolderId={folderId}
                onSynced={invalidate}
            />


            {/* Mover selecionados em massa (pasta existente ou nova) */}
            <BulkMoveDialog
                open={bulkMoveOpen}
                count={selectedIds.size}
                flatFolders={flatFolders}
                currentFolderId={folderId}
                saving={bulkMoveMutation.isPending}
                onClose={() => setBulkMoveOpen(false)}
                onMoveExisting={(targetFolderId) =>
                    bulkMoveMutation.mutate({ ids: Array.from(selectedIds), folderId: targetFolderId })}
                onCreateAndMove={async (folderName) => {
                    const r = await createFolder({ name: folderName, project_id: projectId, parent_id: null });
                    if (r?.id) {
                        bulkMoveMutation.mutate({ ids: Array.from(selectedIds), folderId: r.id });
                    }
                }}
            />

            <ConfirmDialog
                open={!!deleteFolderTarget}
                title="Excluir pasta?"
                description={`A pasta "${deleteFolderTarget?.name}" e suas subpastas serão excluídas. Os templates dentro voltam pra raiz (não são apagados).`}
                confirmLabel="Excluir pasta"
                saving={deleteFolderMutation.isPending}
                onClose={() => setDeleteFolderTarget(null)}
                onConfirm={() => deleteFolderMutation.mutate(deleteFolderTarget!.id)}
            />

            <ConfirmDialog
                open={!!deleteTemplateTarget}
                title="Excluir template?"
                description={`O template "${deleteTemplateTarget?.name}" será excluído permanentemente. Se for uma peça do Template Builder, ela também será removida de "Suas peças".`}
                confirmLabel="Excluir template"
                saving={deleteTemplateMutation.isPending}
                onClose={() => setDeleteTemplateTarget(null)}
                onConfirm={() => deleteTemplateMutation.mutate(deleteTemplateTarget!.id)}
            />

            <ConfirmDialog
                open={bulkDeleteOpen}
                title={`Excluir ${selectedIds.size} ${selectedIds.size === 1 ? 'template' : 'templates'}?`}
                description="Ação permanente — os templates selecionados serão excluídos. Peças do Template Builder entre eles também saem de 'Suas peças'. Esteiras já configuradas pra usar esses templates podem parar de gerar broadcasts."
                confirmLabel={`Excluir ${selectedIds.size}`}
                saving={bulkDeleteMutation.isPending}
                onClose={() => setBulkDeleteOpen(false)}
                onConfirm={() => bulkDeleteMutation.mutate(Array.from(selectedIds))}
            />
        </div>
    );
}

// ─── Card de template próprio ────────────────────────────────────

function CustomTemplateCard({
    template, selected, approvalCounts, onToggleSelect, onEdit, onDelete,
}: {
    template: CustomTemplate;
    selected: boolean;
    approvalCounts?: TemplateApprovalCounts;
    onToggleSelect: () => void;
    onEdit: () => void;
    onDelete: () => void;
}) {
    const isCard = template.template_type === 'generic';
    const buttons = (template.buttons_json ?? []).slice(0, 3);
    const metaBadge = renderMetaStatusBadge(approvalCounts);

    return (
        <article
            onClick={onToggleSelect}
            className={`group bg-gradient-to-b from-gray-50 to-gray-100 border rounded-xl overflow-hidden hover:shadow-md transition-all flex flex-col cursor-pointer ${
                selected ? 'border-violet-400 ring-2 ring-violet-300' : 'hover:border-violet-300'
            }`}
        >
            <div className="flex items-center justify-between px-3 py-2 border-b bg-white/60">
                <div className="flex items-center gap-1.5 min-w-0">
                    {selected
                        ? <CheckCircle2 className="w-4 h-4 text-violet-600 shrink-0" />
                        : <Circle className="w-4 h-4 text-gray-300 shrink-0 group-hover:text-violet-300" />}
                    <Badge variant="outline" className="text-[10px] shrink-0">
                        {isCard || template.image_url ? 'Card' : 'Texto + botões'}
                    </Badge>
                    {template.source === 'cloned' && (
                        <Badge variant="outline" className="text-[9px] px-1.5 py-0 text-sky-600 border-sky-200">
                            clonado
                        </Badge>
                    )}
                    {metaBadge}
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button type="button" onClick={(e) => { e.stopPropagation(); onEdit(); }} title="Editar"
                        className="w-7 h-7 rounded-full hover:bg-gray-200 flex items-center justify-center">
                        <Pencil className="w-3.5 h-3.5 text-gray-600" />
                    </button>
                    <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(); }} title="Excluir"
                        className="w-7 h-7 rounded-full hover:bg-red-50 flex items-center justify-center">
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </button>
                </div>
            </div>

            <div className="p-3 flex-1">
                <div className="bg-white rounded-2xl rounded-tl-sm shadow-sm border border-gray-200 overflow-hidden max-w-[300px]">
                    {template.image_url && (
                        <div className="aspect-[1.91/1] bg-gray-100 overflow-hidden">
                            <img src={template.image_url} alt="" loading="lazy"
                                className="w-full h-full object-contain" />
                        </div>
                    )}
                    <div className="p-2.5 space-y-0.5">
                        {isCard ? (
                            <>
                                {template.title && (
                                    <p className="text-[13px] font-semibold text-gray-900 line-clamp-2 leading-tight">
                                        {template.title}
                                    </p>
                                )}
                                {(template.subtitle || template.text_content) && (
                                    <p className="text-[11px] text-gray-600 line-clamp-3 leading-tight whitespace-pre-line">
                                        {template.subtitle || template.text_content}
                                    </p>
                                )}
                            </>
                        ) : (
                            <p className="text-[12px] text-gray-900 leading-snug whitespace-pre-line line-clamp-5">
                                {template.text_content || template.title || '(sem texto)'}
                            </p>
                        )}
                    </div>
                    {buttons.length > 0 && (
                        <div className="border-t">
                            {buttons.map((b, i) => (
                                <div key={i}
                                    className={`px-3 py-2 text-center text-[12px] text-violet-600 font-medium truncate ${i > 0 ? 'border-t border-gray-100' : ''}`}>
                                    {b.title || '(sem texto)'}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className="px-3 py-2 border-t bg-white/60">
                <p className="text-[11px] text-gray-500 truncate" title={template.name}>
                    {template.name}
                </p>
            </div>
        </article>
    );
}

// ─── Badge de status Meta ────────────────────────────────────────

function renderMetaStatusBadge(counts?: TemplateApprovalCounts) {
    if (!counts || counts.total === 0) {
        return (
            <Badge variant="outline" className="text-[9px] px-1.5 py-0 text-gray-500 border-gray-200"
                title="Template ainda não foi submetido pra aprovação Meta.">
                Meta —
            </Badge>
        );
    }
    if (counts.approved === counts.total) {
        return (
            <Badge variant="outline" className="text-[9px] px-1.5 py-0 text-emerald-600 border-emerald-200"
                title={`Aprovado em ${counts.approved} de ${counts.total} page(s)`}>
                ✓ Meta {counts.approved}
            </Badge>
        );
    }
    if (counts.pending > 0 && counts.rejected === 0 && counts.errored === 0) {
        return (
            <Badge variant="outline" className="text-[9px] px-1.5 py-0 text-amber-600 border-amber-200"
                title={`Aguardando aprovação: ${counts.pending} pendente(s) de ${counts.total} page(s)`}>
                ⏳ Meta {counts.approved}/{counts.total}
            </Badge>
        );
    }
    if (counts.rejected > 0 || counts.errored > 0) {
        const bad = counts.rejected + counts.errored;
        return (
            <Badge variant="outline" className="text-[9px] px-1.5 py-0 text-red-600 border-red-200"
                title={`${bad} rejeição/erro em ${counts.total} page(s) — clique no template pra ver motivo`}>
                ✗ Meta {counts.approved}/{counts.total}
            </Badge>
        );
    }
    return (
        <Badge variant="outline" className="text-[9px] px-1.5 py-0 text-gray-600 border-gray-200">
            Meta {counts.approved}/{counts.total}
        </Badge>
    );
}

// ─── Dialog criar/renomear pasta ─────────────────────────────────

function FolderFormDialog({
    state, projects, defaultProjectId, onClose, onSubmit, saving,
}: {
    state: { open: boolean; mode: 'create' | 'rename'; folder: TemplateFolderNode | null };
    projects: Array<{ id: number; name: string }>;
    defaultProjectId: number | null;
    onClose: () => void;
    // No modo "create": projectId pode ser null (pessoal) ou um id.
    // No modo "rename": projectId é ignorado.
    onSubmit: (name: string, projectId: number | null) => void;
    saving: boolean;
}) {
    const [name, setName] = useState('');
    // String pra `SearchableSelect`. '' = pessoal.
    const [projectValue, setProjectValue] = useState<string>('');

    // Sincroniza o nome + escopo ao abrir
    useEffect(() => {
        if (!state.open) return;
        if (state.mode === 'rename') {
            setName(state.folder?.name ?? '');
        } else {
            setName('');
            // Default do dropdown: projeto atualmente filtrado na tela
            setProjectValue(defaultProjectId == null ? '' : String(defaultProjectId));
        }
    }, [state.open, state.mode, state.folder, defaultProjectId]);

    const submit = () => {
        const trimmed = name.trim();
        if (!trimmed) return;
        const projectId = projectValue ? Number(projectValue) : null;
        onSubmit(trimmed, projectId);
    };

    return (
        <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle>{state.mode === 'create' ? 'Nova pasta' : 'Renomear pasta'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    {state.mode === 'create' && (
                        <div className="space-y-1.5">
                            <Label htmlFor="folder-project">Projeto</Label>
                            <SearchableSelect
                                value={projectValue}
                                onValueChange={setProjectValue}
                                options={[
                                    { value: '', label: 'Pessoal (sem projeto)' },
                                    ...projects.map((p) => ({ value: String(p.id), label: p.name })),
                                ]}
                                placeholder="Selecione o projeto"
                                searchPlaceholder="Buscar projeto..."
                                className="h-9"
                            />
                            <p className="text-[10px] text-gray-500">
                                Define onde a pasta vai aparecer. Pode trocar depois em "Mover".
                            </p>
                        </div>
                    )}
                    <div className="space-y-1.5">
                        <Label htmlFor="folder-name">Nome</Label>
                        <Input
                            id="folder-name" value={name} autoFocus
                            onChange={(e) => setName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) submit(); }}
                            placeholder="Ex: Carros, Imóveis..."
                            maxLength={255}
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
                    <Button onClick={submit} disabled={!name.trim() || saving}>
                        {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                        {state.mode === 'create' ? 'Criar' : 'Salvar'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ─── Dialog mover pasta ──────────────────────────────────────────

function MoveFolderDialog({
    open, folder, flatFolders, projects, onClose, onMovePasta, onMigrateProject, saving,
}: {
    open: boolean;
    folder: TemplateFolderNode | null;
    flatFolders: Array<{ id: number; name: string; depth: number }>;
    projects: Array<{ id: number; name: string }>;
    onClose: () => void;
    onMovePasta: (parentId: number | null) => void;
    onMigrateProject: (newProjectId: number | null) => void;
    saving: boolean;
}) {
    // Não deixa mover pra si mesma. (descendentes são barrados no backend)
    const options = flatFolders.filter((f) => f.id !== folder?.id);
    const currentProjectId = folder?.project_id ?? null;
    // String pra `SearchableSelect`. '' = pessoal. Default = projeto atual da pasta.
    const [projectValue, setProjectValue] = useState<string>('');

    useEffect(() => {
        if (open) {
            setProjectValue(currentProjectId == null ? '' : String(currentProjectId));
        }
    }, [open, currentProjectId]);

    const projectIdNum = projectValue ? Number(projectValue) : null;
    const willMigrate = projectIdNum !== currentProjectId;
    const projectLabel = projectIdNum == null
        ? 'Pessoal (sem projeto)'
        : (projects.find((p) => p.id === projectIdNum)?.name ?? `Projeto #${projectIdNum}`);

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle>Mover "{folder?.name}"</DialogTitle>
                    <DialogDescription>Mover dentro do escopo atual ou migrar pra outro projeto.</DialogDescription>
                </DialogHeader>

                {/* ─── Migrar de escopo (projeto) ─── */}
                <div className="space-y-1.5 border rounded-lg p-3 bg-violet-50/30">
                    <Label className="text-xs">Projeto de destino</Label>
                    <SearchableSelect
                        value={projectValue}
                        onValueChange={setProjectValue}
                        options={[
                            { value: '', label: 'Pessoal (sem projeto)' },
                            ...projects.map((p) => ({ value: String(p.id), label: p.name })),
                        ]}
                        placeholder="Selecione o projeto"
                        searchPlaceholder="Buscar projeto..."
                        className="h-9"
                    />
                    {willMigrate ? (
                        <>
                            <p className="text-[11px] text-violet-900">
                                <strong>Migração em cascata:</strong> a pasta, suas subpastas e
                                todos os templates dentro vão pra <strong>{projectLabel}</strong>.
                                A pasta vira raiz no destino.
                            </p>
                            <Button
                                onClick={() => onMigrateProject(projectIdNum)}
                                disabled={saving}
                                className="w-full"
                            >
                                {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                                Migrar pra {projectLabel}
                            </Button>
                        </>
                    ) : (
                        <p className="text-[11px] text-gray-500">
                            Mantém no escopo atual ({projectLabel}). Use a lista abaixo
                            pra mover entre pastas desse mesmo escopo.
                        </p>
                    )}
                </div>

                {/* ─── Mover dentro do escopo atual (parent_id) ─── */}
                {!willMigrate && (
                    <div className="space-y-1">
                        <p className="text-xs text-gray-500 px-1">Mover pra pasta deste escopo:</p>
                        <div className="max-h-[35vh] overflow-y-auto -mx-2 px-2 space-y-0.5">
                            <button type="button" onClick={() => onMovePasta(null)} disabled={saving}
                                className="w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-gray-100 text-left text-sm">
                                <Files className="w-4 h-4 text-gray-500" /> Raiz (sem pasta)
                            </button>
                            {options.map((f) => (
                                <button key={f.id} type="button" onClick={() => onMovePasta(f.id)} disabled={saving}
                                    className="w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-gray-100 text-left text-sm"
                                    style={{ paddingLeft: `${8 + f.depth * 14}px` }}>
                                    <FolderInput className="w-4 h-4 text-gray-400" /> {f.name}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ─── Dialog mover templates em massa (pasta existente ou nova) ───

function BulkMoveDialog({
    open, count, flatFolders, currentFolderId, saving, onClose, onMoveExisting, onCreateAndMove,
}: {
    open: boolean;
    count: number;
    flatFolders: Array<{ id: number; name: string; depth: number }>;
    currentFolderId: number | null;
    saving: boolean;
    onClose: () => void;
    onMoveExisting: (folderId: number | null) => void;
    onCreateAndMove: (folderName: string) => void;
}) {
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState('');

    useEffect(() => {
        if (open) { setCreating(false); setNewName(''); }
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle>Mover {count} {count === 1 ? 'template' : 'templates'}</DialogTitle>
                    <DialogDescription>Escolha a pasta destino ou crie uma nova.</DialogDescription>
                </DialogHeader>

                {creating ? (
                    <div className="space-y-2">
                        <Label htmlFor="bulk-new-folder">Nome da nova pasta</Label>
                        <Input
                            id="bulk-new-folder" value={newName} autoFocus maxLength={255}
                            onChange={(e) => setNewName(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter' && newName.trim()) onCreateAndMove(newName.trim()); }}
                            placeholder="Ex: Promoções"
                        />
                        <div className="flex items-center gap-2 pt-1">
                            <Button variant="ghost" size="sm" onClick={() => setCreating(false)} disabled={saving}>
                                ← Voltar
                            </Button>
                            <Button size="sm" className="ml-auto"
                                onClick={() => onCreateAndMove(newName.trim())}
                                disabled={!newName.trim() || saving}>
                                {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                                Criar e mover
                            </Button>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="max-h-[45vh] overflow-y-auto -mx-2 px-2 space-y-0.5">
                            <button type="button" onClick={() => onMoveExisting(null)} disabled={saving || currentFolderId === null}
                                className="w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-gray-100 text-left text-sm disabled:opacity-40">
                                <Files className="w-4 h-4 text-gray-500" /> Raiz (sem pasta)
                            </button>
                            {flatFolders.map((f) => (
                                <button key={f.id} type="button" onClick={() => onMoveExisting(f.id)}
                                    disabled={saving || currentFolderId === f.id}
                                    className="w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-gray-100 text-left text-sm disabled:opacity-40"
                                    style={{ paddingLeft: `${8 + f.depth * 14}px` }}>
                                    <FolderInput className="w-4 h-4 text-gray-400" /> {f.name}
                                </button>
                            ))}
                        </div>
                        <DialogFooter className="sm:justify-between">
                            <Button variant="outline" size="sm" onClick={() => setCreating(true)} disabled={saving}>
                                <Plus className="w-4 h-4 mr-1.5" /> Nova pasta
                            </Button>
                            <Button variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

// ─── Dialog de confirmação genérico ──────────────────────────────

function ConfirmDialog({
    open, title, description, confirmLabel, saving, onClose, onConfirm,
}: {
    open: boolean;
    title: string;
    description: string;
    confirmLabel: string;
    saving: boolean;
    onClose: () => void;
    onConfirm: () => void;
}) {
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-red-500" /> {title}
                    </DialogTitle>
                    <DialogDescription>{description}</DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
                    <Button variant="destructive" onClick={onConfirm} disabled={saving}>
                        {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                        {confirmLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
